import React from "react";
import { Box, TableCell, Typography } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import WarningIcon from "@icons/myicons/warning-circle.svg?react";
import { BookingType, InvestmentInfo, TableRow, Tooltip } from "@components";
import { toCurrency, toFirstTwoSignificantDecimal } from "@helpers";
import { ButtonWithActionMenu } from "@components/ButtonWithActionMenu";
import { ActionMenu } from "@features/csvDrop/ActionMenu";
import { CsvBookingRow } from "@bn/api/apiv3";
import { shouldIgnoreFieldError } from "@features/csvDrop/shouldIgnoreFieldError";

const getDate = (date: string | undefined) =>
  date && isValid(parseISO(date))
    ? format(new Date(date), "dd.MM.yyyy")
    : undefined;

type Props = {
  parsingResult: CsvBookingRow;
  image?: string | null;
  onEditClick: (parsingResult: CsvBookingRow) => void;
  onDeleteClick: (parsingResult: CsvBookingRow) => void;
};

export const DepotsParsedRow = ({
  parsingResult,
  image,
  onEditClick,
  onDeleteClick,
}: Props) => {
  const { t } = useTranslation();

  const errors: string[] = Object.values(parsingResult).reduce((acc, field) => {
    if (field == null || typeof field !== "object") return acc;
    if (shouldIgnoreFieldError(field.name)) return acc;

    return field?.error
      ? [
          ...acc,
          t("csvImport.parsingResults.error", {
            name: t(`csvImport.parsingResults.${field.name}`),
          }),
        ]
      : acc;
  }, []);
  const currency = parsingResult.currency?.value || "EUR";

  return (
    <TableRow
      sx={{
        "& td": {
          verticalAlign: "middle",
        },
      }}
    >
      <TableCell>
        <Box display="flex" alignItems="center" gap={2.5} mb={0.5}>
          {parsingResult.transaction_type?.value && (
            <BookingType type={parsingResult.transaction_type?.value} />
          )}
          {Boolean(errors.length) && (
            <Tooltip
              text={
                <Typography
                  variant="body3"
                  color="text.secondary"
                  fontWeight={400}
                  pl={3}
                >
                  {errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </Typography>
              }
            >
              <Box display="flex" alignItems="center" color="icon.warning">
                <WarningIcon width={24} height={24} />
              </Box>
            </Tooltip>
          )}
        </Box>
        <Typography variant="body3" color="textSecondary">
          {getDate(parsingResult.date?.value)}
        </Typography>
      </TableCell>
      <TableCell>
        <InvestmentInfo
          item={{
            hasValidIsin: Boolean(parsingResult.isin?.value),
            isin: parsingResult.isin?.value,
            name: parsingResult.name?.value,
            type: parsingResult.asset_type?.value,
          }}
          image={image}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body4">
          {toFirstTwoSignificantDecimal(
            parsingResult.number_of_lots?.value || 0
          )}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body4">
          {toCurrency(parsingResult.security_price?.value, currency)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body4" align="right">
          {toCurrency(parsingResult.commission?.value, currency)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body4" align="right" whiteSpace="nowrap">
          {toCurrency(parsingResult.tax_amount?.value, currency)}
        </Typography>
      </TableCell>
      <TableCell>
        <ButtonWithActionMenu>
          <ActionMenu
            onEditClick={() => onEditClick(parsingResult)}
            onDeleteClick={() => onDeleteClick(parsingResult)}
          />
        </ButtonWithActionMenu>
      </TableCell>
    </TableRow>
  );
};
